import type { State } from '@multiplekex/shallot';
import { StepController } from './step-plugin';

export interface StepContent {
  title: string;
  description: string;
}

export interface StepUIOptions {
  steps: StepContent[];
  getStep: () => number;
  setStep: (step: number) => void;
}

export interface StepUIElements {
  btnPrev: HTMLButtonElement | null;
  btnNext: HTMLButtonElement | null;
  counter: HTMLElement | null;
  title: HTMLElement | null;
  description: HTMLElement | null;
}

export function findStepElements(container: HTMLElement | string): StepUIElements {
  const root = typeof container === 'string' ? document.querySelector(container) : container;
  if (!root) {
    return { btnPrev: null, btnNext: null, counter: null, title: null, description: null };
  }

  return {
    btnPrev: root.querySelector('.btn-prev') as HTMLButtonElement | null,
    btnNext: root.querySelector('.btn-next') as HTMLButtonElement | null,
    counter: root.querySelector('.step-counter'),
    title: root.querySelector('.step-title'),
    description: root.querySelector('.step-description'),
  };
}

export function setupStepUI(elements: StepUIElements, options: StepUIOptions): () => void {
  const { steps, getStep, setStep } = options;
  const maxStep = steps.length - 1;

  const stepInfo = elements.title?.parentElement;
  if (stepInfo) {
    let maxHeight = 0;
    for (const step of steps) {
      if (elements.title) elements.title.textContent = step.title;
      if (elements.description) elements.description.innerHTML = step.description;
      maxHeight = Math.max(maxHeight, stepInfo.offsetHeight);
    }
    stepInfo.style.minHeight = `${maxHeight}px`;
  }

  function updateDisplay(): void {
    const currentStep = getStep();
    const { btnPrev, btnNext, counter, title, description } = elements;
    const content = steps[currentStep];

    if (btnPrev) btnPrev.disabled = currentStep <= 0;
    if (btnNext) btnNext.disabled = currentStep >= maxStep;
    if (counter) counter.textContent = `Step ${currentStep + 1} of ${steps.length}`;
    if (title) title.textContent = content?.title ?? '';
    if (description) description.innerHTML = content?.description ?? '';
  }

  const handlePrev = () => setStep(getStep() - 1);
  const handleNext = () => setStep(getStep() + 1);
  const handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft') setStep(getStep() - 1);
    if (e.key === 'ArrowRight') setStep(getStep() + 1);
  };

  elements.btnPrev?.addEventListener('click', handlePrev);
  elements.btnNext?.addEventListener('click', handleNext);
  document.addEventListener('keydown', handleKeydown);

  // Poll for changes to keep UI in sync with source of truth
  const interval = setInterval(updateDisplay, 50);
  updateDisplay();

  return () => {
    clearInterval(interval);
    elements.btnPrev?.removeEventListener('click', handlePrev);
    elements.btnNext?.removeEventListener('click', handleNext);
    document.removeEventListener('keydown', handleKeydown);
  };
}

export function connectStepControls(
  canvas: HTMLCanvasElement,
  state: State,
  steps: StepContent[],
  onStepChange?: (step: number) => void
): () => void {
  const controllers = [...state.query([StepController])];
  const controllerEid = controllers[0];

  if (controllerEid === undefined) {
    return () => {};
  }

  const maxStep = steps.length - 1;

  const getStep = () => StepController.target[controllerEid];
  const setStep = (step: number) => {
    const clamped = Math.max(0, Math.min(maxStep, step));
    StepController.target[controllerEid] = clamped;
    onStepChange?.(clamped);
  };
  const setStepImmediate = (step: number) => {
    const clamped = Math.max(0, Math.min(maxStep, step));
    StepController.step[controllerEid] = clamped;
    StepController.target[controllerEid] = clamped;
    onStepChange?.(clamped);
  };

  const elements = findStepElements(document.body);
  const dispose = setupStepUI(elements, { steps, getStep, setStep });

  canvas.__controllerEid__ = controllerEid;
  canvas.__getStep__ = getStep;
  canvas.__setStep__ = setStep;
  canvas.__setStepImmediate__ = setStepImmediate;
  canvas.__maxStep__ = maxStep;

  return dispose;
}
